import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MDBBadge,
  MDBCard,
  MDBCardBody,
  MDBCollapse,
  MDBCollapseHeader,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import {
  SetActiveSupplyGroups,
  ToggleSupplyGroup,
} from "../../../../../services/redux/slices/diagnostics/clinic/clinicInventory";

import {
  filterSupplyGroupsByMonthYear,
  formatDate,
  formatDateTime,
  formatPersonName,
  getAvailableQuantity,
  getMedicineDosage,
  getStatusColor,
  groupInventoriesBySupply,
} from "./utils";

export default function Body() {
  const dispatch = useDispatch();
  const {
    collections = [],
    activeGroupIds = [],
    selectedMonth,
    selectedYear,
    isLoading = false,
  } = useSelector(({ clinicInventory }) => clinicInventory);

  const groupedSupplies = React.useMemo(
    () => groupInventoriesBySupply(collections),
    [collections],
  );
  const groups = React.useMemo(
    () =>
      filterSupplyGroupsByMonthYear(
        groupedSupplies,
        selectedMonth,
        selectedYear,
      ),
    [groupedSupplies, selectedMonth, selectedYear],
  );

  React.useEffect(() => {
    const validGroupIds = groups.map((group) => String(group._id));
    const survivingActiveIds = activeGroupIds.filter((groupId) =>
      validGroupIds.includes(String(groupId)),
    );
    const fallbackActiveIds =
      survivingActiveIds.length > 0
        ? survivingActiveIds
        : validGroupIds.length
          ? [validGroupIds[0]]
          : [];

    if (
      fallbackActiveIds.length !== activeGroupIds.length ||
      fallbackActiveIds.some(
        (groupId, index) => groupId !== activeGroupIds[index],
      )
    ) {
      dispatch(SetActiveSupplyGroups(fallbackActiveIds));
    }
  }, [dispatch, groups, activeGroupIds]);

  if (!isLoading && !groups.length) {
    return (
      <div className="text-center py-5 text-muted">
        No clinic supplies found for the selected clinics.
      </div>
    );
  }

  return (
    <div className="md-accordion">
      {groups.map((group, index) => {
        const { supply = {}, inventories = [], inventoryCount } = group;
        const isOpen = activeGroupIds.includes(group._id);
        const supplierName = formatPersonName(supply?.supplier);
        const createdByName = formatPersonName(supply?.user);
        const verifiedByName = formatPersonName(supply?.verifiedBy);

        return (
          <MDBCard key={group._id || index} className="mb-3 shadow-sm">
            <MDBCollapseHeader
              onClick={() => dispatch(ToggleSupplyGroup(group._id))}
              className="d-flex justify-content-between align-items-center bg-white"
              style={{ cursor: "pointer" }}
            >
              <div className="pr-3">
                <div
                  className="d-flex align-items-center flex-wrap mb-2"
                  style={{ gap: "8px" }}
                >
                  <h6 className="mb-0 font-weight-bold text-primary">
                    {supplierName}
                  </h6>
                  <MDBBadge color={getStatusColor(supply?.status)}>
                    {String(supply?.status || "draft").toUpperCase()}
                  </MDBBadge>
                </div>
                <div className="small text-muted">
                  Created {formatDateTime(supply?.createdAt)} by {createdByName}
                </div>
                <div className="small text-muted">
                  Verified by {verifiedByName} | Verified at{" "}
                  {formatDateTime(supply?.verifyAt)}
                </div>
                <div className="small text-muted">
                  Remarks: {supply?.remarks || "No remarks"}
                </div>
              </div>

              <div className="text-right">
                <div className="small text-muted">
                  {inventoryCount} batch{inventoryCount === 1 ? "" : "es"}
                </div>
                <MDBIcon
                  icon={isOpen ? "chevron-up" : "chevron-down"}
                  className="mt-2 text-primary"
                />
              </div>
            </MDBCollapseHeader>

            <MDBCollapse isOpen={isOpen}>
              <MDBCardBody className="pt-0">
                <MDBTable responsive hover bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Batch No</th>
                      <th>Expiry</th>
                      <th>Quantity</th>
                      <th>Available</th>
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventories.map((inventory, childIndex) => {
                      const quantity = Number(inventory?.qnty || 0);
                      const available = getAvailableQuantity(inventory);
                      const unit = inventory?.unit || "pcs";
                      const itemName =
                        inventory?.menu?.abbreviation || "Unnamed item";
                      const dosage = getMedicineDosage(inventory?.menu);

                      return (
                        <tr
                          key={inventory?._id || `${group._id}-${childIndex}`}
                        >
                          <td>{childIndex + 1}</td>
                          <td>
                            <div className="font-weight-bold">
                              {dosage ? `${itemName} (${dosage})` : itemName}
                            </div>
                            <div className="small text-muted">
                              {inventory?.menu?.description || "No description"}
                            </div>
                          </td>
                          <td>{inventory?.batchNo || "N/A"}</td>
                          <td>{formatDate(inventory?.expiry)}</td>
                          <td>
                            {quantity} {unit}
                          </td>
                          <td>
                            {available} {unit}
                          </td>
                          <td>
                            <MDBBadge color={getStatusColor(inventory?.status)}>
                              {String(
                                inventory?.status || "available",
                              ).toUpperCase()}
                            </MDBBadge>
                          </td>
                          <td>{inventory?.remarks || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </MDBTable>
              </MDBCardBody>
            </MDBCollapse>
          </MDBCard>
        );
      })}
    </div>
  );
}
